import * as Sentry from '@sentry/react';

import { sentryOptions } from '../constants/sentryOptions';
import { ErrorReportingClient } from '../types/ErrorReportingClient';
import {
  ErrorReportingScopeSnapshot,
  ScopeBreadcrumb,
} from '../types/ErrorReportingScopeSnapshot';
import { ErrorReportingUser } from '../types/ErrorReportingUser';
import { TagValue } from '../types/TagValue';

/**
 * Adapter that bridges the vendor-neutral `ErrorReportingClient`
 * contract to the Sentry SDK.
 *
 * All Sentry-specific knowledge lives here:
 *   - `init` owns `sentryOptions` (the store passes no args).
 *   - `wrapCreateBrowserRouter` bridges Sentry's `*V7`-suffixed export.
 *   - `getScopeSnapshot` merges Sentry's global + isolation scopes into
 *     the flattened shape the devtools consume.
 *   - `clearScope` resets the isolation scope (the one we mutate at
 *     runtime via `setUser`, `setTag`, etc.).
 *
 * To swap vendors, write a sibling file (ex. `rollbarClient.ts`) that
 * exports an `ErrorReportingClient` and update `useErrorReportingStore`
 * to import from it.
 */
const buildScopeSnapshot = (): ErrorReportingScopeSnapshot => {
  const isolation = Sentry.getIsolationScope().getScopeData();
  const global = Sentry.getGlobalScope().getScopeData();

  return {
    breadcrumbs: isolation.breadcrumbs as ScopeBreadcrumb[],
    contexts: { ...global.contexts, ...isolation.contexts },
    extra: { ...global.extra, ...isolation.extra },
    tags: { ...global.tags, ...isolation.tags } as Record<string, TagValue>,
    user: isolation.user as ErrorReportingUser | undefined,
  };
};

export const sentryClient: ErrorReportingClient = {
  addBreadcrumb: (breadcrumb) => {
    Sentry.addBreadcrumb(breadcrumb);
  },
  captureException: (error, context) => {
    Sentry.captureException(error, context);
  },
  captureMessage: (message, context) => {
    Sentry.captureMessage(message, context);
  },
  clearScope: () => {
    Sentry.getIsolationScope().clear();
  },
  flush: (timeout) => Sentry.flush(timeout),
  getScopeSnapshot: buildScopeSnapshot,
  init: () => Sentry.init(sentryOptions),
  setExtras: (data) => {
    Sentry.setExtras(data);
  },
  setTag: (key, value) => {
    Sentry.setTag(key, value);
  },
  setUser: (user) => {
    Sentry.setUser(user);
  },
  wrapCreateBrowserRouter: (createRouter) =>
    Sentry.wrapCreateBrowserRouterV7(createRouter),
};
